import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Sparkles,
  Edit3,
  Trash2,
  ShieldCheck,
  RefreshCw,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  card,
  microLabel,
  sectionTitle,
  accentBarSection,
  bodySecondary,
  motionStaggerChild,
  emptyState,
  iconSize,
} from "../../../../../lib/design-system";
import { cn } from "../../../../../lib/utils";
import {
  listPOs,
  createPO,
  updatePO,
  deletePO,
  seedStandardPOs,
} from "../../../../../services/program.service";
import OutcomeForm from "../OutcomeForm";

const POsTab = ({ programId }) => {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchPOs = async () => {
    try {
      setLoading(true);
      const data = await listPOs(programId);
      setPos(data.pos || []);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load POs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPOs();
  }, [programId]);

  const handleSeed = async () => {
    if (
      !window.confirm(
        "Seed the 12 GAPC standard POs? This only runs once per program."
      )
    )
      return;
    setSeeding(true);
    try {
      const result = await seedStandardPOs(programId);
      toast.success(`Seeded ${result.created} standard POs`);
      fetchPOs();
    } catch (err) {
      toast.error(err.response?.data?.error || "Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  const handleDelete = async (po) => {
    if (
      !window.confirm(
        `Delete ${po.code}? This is a soft delete and can be reactivated via API.`
      )
    )
      return;
    try {
      await deletePO(programId, po._id);
      toast.success(`${po.code} deleted`);
      fetchPOs();
    } catch (err) {
      toast.error(err.response?.data?.error || "Delete failed");
    }
  };

  const handleFormSubmit = async (payload) => {
    if (editing) {
      await updatePO(programId, editing._id, payload);
    } else {
      await createPO(programId, payload);
    }
    fetchPOs();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h2 className={cn(sectionTitle, "text-xl sm:text-2xl")}>
            Program Outcomes
          </h2>
          <div className={cn(accentBarSection, "mt-2")} />
          <p className={cn(bodySecondary, "mt-3 max-w-2xl")}>
            The 12 standard NBA / Washington Accord graduate attributes per
            program. Seed the standard set in one click, or author custom POs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchPOs}
            className="px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw className={iconSize.md} />
          </button>
          {pos.length === 0 && (
            <button
              onClick={handleSeed}
              disabled={seeding}
              className={cn(
                "inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-accent1 text-accent1 font-semibold transition-colors",
                seeding ? "opacity-60 cursor-not-allowed" : "hover:bg-accent2"
              )}
            >
              <Sparkles className={iconSize.md} />
              {seeding ? "Seeding…" : "Seed 12 GAPC POs"}
            </button>
          )}
          <button
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            className="inline-flex items-center gap-2 bg-accent1 text-white font-semibold px-4 py-2 rounded-sm hover:opacity-90 transition-opacity"
          >
            <Plus className={iconSize.md} />
            Add PO
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <SkeletonList />
      ) : pos.length === 0 ? (
        <div className={emptyState}>
          <div className="mx-auto h-14 w-14 rounded-sm bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
            <Sparkles className="h-7 w-7 text-gray-400" />
          </div>
          <div className="font-bold text-gray-700 dark:text-gray-200">
            No Program Outcomes yet
          </div>
          <div className={cn(bodySecondary, "mt-1")}>
            Click "Seed 12 GAPC POs" to populate the standard NBA list, or
            author custom ones.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {pos.map((po, i) => (
            <motion.div
              key={po._id}
              {...motionStaggerChild(i, 0)}
              className={cn(card, "p-5 group")}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="h-1 w-8 bg-accent1 rounded-sm mb-2" />
                  <div className={microLabel}>{po.code}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    {po.category && (
                      <span className="px-2.5 py-1 rounded-sm bg-accent2 text-accent1 text-xs font-semibold">
                        {po.category}
                      </span>
                    )}
                    {po.isStandard && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700 text-xs font-medium">
                        <ShieldCheck className="h-3 w-3" /> Standard
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                    {po.statement}
                  </p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button
                    onClick={() => {
                      setEditing(po);
                      setShowForm(true);
                    }}
                    className="p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Edit"
                    aria-label="Edit"
                  >
                    <Edit3 className={iconSize.sm} />
                  </button>
                  <button
                    onClick={() => handleDelete(po)}
                    className="p-2 rounded-sm hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600"
                    title="Delete"
                    aria-label="Delete"
                  >
                    <Trash2 className={iconSize.sm} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {showForm && (
        <OutcomeForm
          type="PO"
          outcome={editing}
          onSubmit={handleFormSubmit}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

const SkeletonList = () => (
  <div className="space-y-3">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className={cn(card, "p-5")}>
        <div className="flex gap-4">
          <div className="space-y-2">
            <div className="h-1 w-8 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
            <div className="h-3 w-10 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
          </div>
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
            <div className="h-3 w-full bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
            <div className="h-3 w-3/4 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export default POsTab;
